const MS_DAY = 24 * 60 * 60 * 1000;
const MS_30 = 30 * MS_DAY;

export function getInvoiceTotal(inv) {
  const raw = inv?.orderData?.total;
  if (raw != null && raw !== "") return Number(raw) || 0;
  const amt = inv?.amount;
  if (typeof amt === "string") {
    return parseFloat(String(amt).replace(/[^0-9.\-]/g, "")) || 0;
  }
  return Number(amt) || 0;
}

function isCancelled(inv) {
  const os = String(inv?.orderData?.order_status || "").toLowerCase();
  const ps = String(inv?.orderData?.payment_status || inv?.status || "").toLowerCase();
  if (["cancelled", "canceled", "cancel", "refunded"].includes(os)) return true;
  if (["cancelled", "canceled", "cancel"].includes(ps)) return true;
  return false;
}

function isPaid(inv) {
  if (isCancelled(inv)) return false;
  const ps = String(inv?.orderData?.payment_status || inv?.status || "").toLowerCase();
  return ps === "paid";
}

function isUnpaid(inv) {
  if (isCancelled(inv)) return false;
  return !isPaid(inv);
}

function getCreatedMs(inv) {
  const d = inv?.orderData?.created_at || inv?.date;
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : 0;
}

function pctChange(recent, older) {
  if (older > 0) {
    const p = ((recent - older) / older) * 100;
    const sign = p >= 0 ? "+" : "";
    return { text: `${sign}${p.toFixed(2)} %`, positive: p >= 0 };
  }
  if (recent > 0) return { text: "+100 %", positive: true };
  return { text: "0 %", positive: true };
}

function sumInWindow(invoices, filter, windowStart, windowEnd) {
  let sum = 0;
  for (const inv of invoices) {
    const t = getCreatedMs(inv);
    if (t < windowStart || t >= windowEnd) continue;
    if (!filter(inv)) continue;
    sum += getInvoiceTotal(inv);
  }
  return sum;
}

/**
 * Builds the four invoice dashboard cards from order-backed invoice rows.
 * Percent change compares last 30 days vs the prior 30 days (revenue in each bucket).
 */
export function buildInvoiceWidgets(invoices) {
  const list = Array.isArray(invoices) ? invoices : [];
  const now = Date.now();
  const recentStart = now - MS_30;
  const olderStart = now - MS_30 * 2;
  const olderEnd = recentStart;

  const defs = [
    { id: 1, label: "Invoices Sent", caption: "Invoices sent", feaIcon: "file-text", filter: () => true },
    { id: 2, label: "Paid Invoices", caption: "Paid by clients", feaIcon: "check-square", filter: isPaid },
    { id: 3, label: "Unpaid Invoices", caption: "Unpaid by clients", feaIcon: "clock", filter: isUnpaid },
    { id: 4, label: "Cancelled Invoices", caption: "Cancelled by clients", feaIcon: "x-octagon", filter: isCancelled },
  ];

  return defs.map((def) => {
    let totalSum = 0;
    let totalCount = 0;
    for (const inv of list) {
      if (!def.filter(inv)) continue;
      totalSum += getInvoiceTotal(inv);
      totalCount += 1;
    }

    const recentSum = sumInWindow(list, def.filter, recentStart, now);
    const olderSum = sumInWindow(list, def.filter, olderStart, olderEnd);
    const pct = pctChange(recentSum, olderSum);
    const counterK = Math.round((totalSum / 1000) * 100) / 100;

    return {
      id: def.id,
      label: def.label,
      percentage: pct.text,
      percentageClass: pct.positive ? "success" : "danger",
      counter: counterK,
      badge: totalCount.toLocaleString("en-GB"),
      caption: def.caption,
      feaIcon: def.feaIcon,
      prefix: "£",
      suffix: "k",
    };
  });
}
